import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import React, { useEffect, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import classNames from 'classnames';
import Button from '../button';
import Icon from '../icon';
import ConfigProvider from '../config-provider';
import getScrollBarSize from '../../utils/getScrollBar';
import setStyle from '../../utils/setStyle';

var stopPropagation = function stopPropagation(e) {
  e.stopPropagation();
}; // 动画时间


var ANIMATE_TIME = 200;

var ModalPortal = function ModalPortal(_ref) {
  var _classNames;

  var _ref$size = _ref.size,
      size = _ref$size === void 0 ? 'normal' : _ref$size,
      _ref$autoFocus = _ref.autoFocus,
      autoFocus = _ref$autoFocus === void 0 ? 'confirm' : _ref$autoFocus,
      _ref$showCancel = _ref.showCancel,
      showCancel = _ref$showCancel === void 0 ? true : _ref$showCancel,
      _ref$showConfirm = _ref.showConfirm,
      showConfirm = _ref$showConfirm === void 0 ? true : _ref$showConfirm,
      _ref$showClose = _ref.showClose,
      showClose = _ref$showClose === void 0 ? true : _ref$showClose,
      title = _ref.title,
      cancelText = _ref.cancelText,
      confirmText = _ref.confirmText,
      customizePrefixCls = _ref.prefixCls,
      children = _ref.children,
      center = _ref.center,
      subtitle = _ref.subtitle,
      isCancelDisabled = _ref.isCancelDisabled,
      isCancelLoading = _ref.isCancelLoading,
      isConfirmDisabled = _ref.isConfirmDisabled,
      isConfirmLoading = _ref.isConfirmLoading,
      content = _ref.content,
      footerAssist = _ref.footerAssist,
      width = _ref.width,
      contentSlot = _ref.contentSlot,
      visible = _ref.visible,
      closeOnClickMask = _ref.closeOnClickMask,
      emitClose = _ref.emitClose,
      onClose = _ref.onClose,
      onCancel = _ref.onCancel,
      onConfirm = _ref.onConfirm,
      onAfterClose = _ref.onAfterClose,
      onAfterOpen = _ref.onAfterOpen;
  var cacheStyle = useRef({});
  var cacheOverflow = useRef({});
  var modalBodyRef = useRef();

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      overHeight = _useState2[0],
      setOverHeight = _useState2[1];
  /** 当关闭 modal 时，应该执行的函数 */


  var afterClose = function afterClose() {
    setTimeout(function () {
      setStyle(cacheOverflow.current);
      cacheOverflow.current = {};
      setStyle(cacheStyle.current);
      cacheStyle.current = {};

      if (onAfterClose) {
        onAfterClose();
      }
    }, ANIMATE_TIME);
  };
  /** 在打开 modal 之后应该执行的函数 */


  var afterOpen = function afterOpen() {
    var scrollBarSize = getScrollBarSize();

    if (scrollBarSize) {
      cacheStyle.current = setStyle({
        position: 'relative',
        width: "calc(100% - ".concat(scrollBarSize, "px)")
      });
    }

    cacheOverflow.current = setStyle({
      overflow: 'hidden'
    });
    var modalBody = modalBodyRef.current;

    if (modalBody) {
      var newOverHeight = modalBody.scrollHeight > modalBody.clientHeight;

      if (overHeight !== newOverHeight) {
        setOverHeight(newOverHeight);
      }
    }

    if (onAfterOpen) {
      onAfterOpen();
    }
  };

  var firstRenderRef = React.useRef(true);
  useEffect(function () {
    if (visible) {
      afterOpen();
    } else if (!firstRenderRef.current) {
      afterClose();
    }

    if (firstRenderRef.current) {
      firstRenderRef.current = false;
    } // only watch visible
    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [visible]);

  var handleCancel = function handleCancel() {
    if (onCancel) {
      onCancel();
    }
  };

  var handleConfirm = function handleConfirm() {
    if (onConfirm) {
      onConfirm();
    }
  };

  var handleClose = function handleClose() {
    if (emitClose) {
      onClose();
    } else {
      handleCancel();
    }
  };

  var cancelOnMask = function cancelOnMask(e) {
    e.stopPropagation();

    if (closeOnClickMask) {
      handleCancel();
    }
  };

  var _React$useContext = React.useContext(ConfigProvider.ConfigContext),
      getPrefixCls = _React$useContext.getPrefixCls,
      contextLocale = _React$useContext.locale;

  var prefixCls = getPrefixCls('modal', customizePrefixCls);
  var locale = contextLocale.Modal;
  var contentClass = classNames("".concat(prefixCls, "__content"), (_classNames = {}, _defineProperty(_classNames, "".concat(prefixCls, "__content--").concat(size), size), _defineProperty(_classNames, "".concat(prefixCls, "__content--center"), center), _classNames));
  var contentStyle = width ? {
    width: width
  } : undefined;
  var bodyClass = classNames("".concat(prefixCls, "__body"), {
    'over-height': overHeight
  });
  var showFooter = !!(footerAssist || showCancel || showConfirm);
  var footerClass = classNames("".concat(prefixCls, "__footer"), {
    'with-assist': footerAssist
  });
  return (
    /*#__PURE__*/
    React.createElement(CSSTransition, {
      in: visible,
      unmountOnExit: true,
      timeout: ANIMATE_TIME,
      classNames: "".concat(prefixCls, "-fade-fast")
    },
    /*#__PURE__*/
    React.createElement("div", {
      className: "".concat(prefixCls, "__mask")
    },
    /*#__PURE__*/
    React.createElement("div", {
      className: "".concat(prefixCls, "__container"),
      onClick: cancelOnMask
    },
    /*#__PURE__*/
    React.createElement(CSSTransition, {
      in: visible,
      unmountOnExit: true,
      timeout: 200,
      classNames: "".concat(prefixCls, "-move-down-fast")
    },
    /*#__PURE__*/
    React.createElement("div", {
      className: "".concat(prefixCls, "__box"),
      onClick: stopPropagation
    },
    /*#__PURE__*/
    React.createElement("div", {
      className: contentClass,
      style: contentStyle
    }, contentSlot ||
    /*#__PURE__*/
    React.createElement(React.Fragment, null,
    /*#__PURE__*/
    React.createElement("div", {
      className: "".concat(prefixCls, "__header")
    },
    /*#__PURE__*/
    React.createElement("div", {
      className: "".concat(prefixCls, "__header-inner")
    },
    /*#__PURE__*/
    React.createElement("div", {
      className: "".concat(prefixCls, "__title")
    }, title !== null && title !== void 0 ? title : locale.title), !!subtitle &&
    /*#__PURE__*/
    React.createElement("div", {
      className: "".concat(prefixCls, "__subtitle")
    }, subtitle))),
    /*#__PURE__*/
    React.createElement("div", {
      ref: modalBodyRef,
      className: bodyClass
    }, children || content), showFooter &&
    /*#__PURE__*/
    React.createElement("div", {
      className: footerClass
    }, footerAssist,
    /*#__PURE__*/
    React.createElement("div", {
      className: "".concat(prefixCls, "__footer-buttons")
    }, showCancel &&
    /*#__PURE__*/
    React.createElement(Button, {
      onClick: handleCancel,
      disabled: isCancelDisabled,
      loading: isCancelLoading,
      autoFocus: autoFocus === 'cancel'
    }, cancelText !== null && cancelText !== void 0 ? cancelText : locale.cancelText), showConfirm &&
    /*#__PURE__*/
    React.createElement(Button, {
      type: "primary",
      onClick: handleConfirm,
      disabled: isConfirmDisabled,
      loading: isConfirmLoading,
      autoFocus: autoFocus === 'confirm'
    }, confirmText !== null && confirmText !== void 0 ? confirmText : locale.confirmText))))), !!showClose &&
    /*#__PURE__*/
    React.createElement("span", {
      onClick: handleClose
    },
    /*#__PURE__*/
    React.createElement(Icon, {
      className: "".concat(prefixCls, "__close"),
      type: "close"
    })))))))
  );
};

export default ModalPortal;